import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router';
import { PermissionGate } from '@saas/ui';
import { SURVEY_MANAGE_PERMISSIONS, SURVEY_READ_PERMISSIONS, SURVEY_WRITE_PERMISSIONS } from './nav.js';

/** apps/web が集約する。ルート単位で遅延ロード（03 第 I 部 §2） */
type PageModule = { default: ComponentType };

function page(load: () => Promise<PageModule>): RouteObject['lazy'] {
  return async () => {
    const m = await load();
    return { Component: m.default };
  };
}

function guarded(load: () => Promise<PageModule>, anyOf: string[]): RouteObject['lazy'] {
  return async () => {
    const m = await load();
    const Page = m.default;
    const Guarded = () => (
      <PermissionGate anyOf={anyOf}>
        <Page />
      </PermissionGate>
    );
    return { Component: Guarded };
  };
}

/**
 *   /survey                                回答する（自分宛て）と作成した調査の一覧
 *   /survey/new                            作成（write）
 *   /survey/take/:id                       回答画面（survey.response.write。ガードはサーバ側）
 *   /survey/surveys/:id                    詳細（概要 / 集計 / 受講記録）
 *   /survey/surveys/:id/edit               編集（下書き = write / 公開中 = manage。状態ごとの判定はサーバ側）
 *   /survey/surveys/:id/certificates/:uid  受講証明書（印刷用 HTML と PDF のダウンロード）
 */
export const surveyRoutes: RouteObject[] = [
  { path: '/survey/create', lazy: guarded(() => import('./pages/FormCreatePage.js'), SURVEY_WRITE_PERMISSIONS) },
  { path: '/survey/forms', lazy: guarded(() => import('./pages/ExternalFormListPage.js'), SURVEY_READ_PERMISSIONS) },
  { path: '/survey/forms/new', lazy: guarded(() => import('./pages/FormEditorPage.js'), SURVEY_WRITE_PERMISSIONS) },
  { path: '/survey/forms/:id', lazy: guarded(() => import('./pages/FormEditorPage.js'), SURVEY_READ_PERMISSIONS) },
  { path: '/survey', lazy: page(() => import('./pages/SurveyListPage.js')) },
  { path: '/survey/new', lazy: guarded(() => import('./pages/FormEditorPage.js'), SURVEY_WRITE_PERMISSIONS) },
  { path: '/survey/take/:id', lazy: page(() => import('./pages/RespondPage.js')) },
  { path: '/survey/surveys/:id', lazy: guarded(() => import('./pages/SurveyDetailPage.js'), SURVEY_READ_PERMISSIONS) },
  { path: '/survey/surveys/:id/edit', lazy: guarded(() => import('./pages/FormEditorPage.js'), [...SURVEY_WRITE_PERMISSIONS, ...SURVEY_MANAGE_PERMISSIONS]) },
  { path: '/survey/surveys/:id/certificates/:userId', lazy: guarded(() => import('./pages/CertificatePage.js'), ['survey.record.read']) },
];
export const surveyPublicRoutes: RouteObject[] = [
  { path: '/forms/receipt/:token', lazy: page(() => import('./pages/FormReceiptPage.js')) },
  { path: '/forms/:token', lazy: page(() => import('./pages/PublicFormPage.js')) },
];
